/**
 * @file LanguageSwitcher.tsx
 * @description Material Design language switcher with flag icons
 * @module components/LanguageSwitcher
 */

'use client';

import { useLocale } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/routing';
import { useParams } from 'next/navigation';
import { useState, useRef, useEffect } from 'react';

type Locale = 'pl' | 'en' | 'de' | 'cs' | 'sk' | 'uk';

interface LocaleOption {
  code: Locale;
  flag: string;
  label: string;
  nativeName: string;
}

const locales: LocaleOption[] = [
  { code: 'pl', flag: '🇵🇱', label: 'Polish', nativeName: 'Polski' },
  { code: 'en', flag: '🇬🇧', label: 'English', nativeName: 'English' },
  { code: 'de', flag: '🇩🇪', label: 'German', nativeName: 'Deutsch' },
  { code: 'cs', flag: '🇨🇿', label: 'Czech', nativeName: 'Čeština' },
  { code: 'sk', flag: '🇸🇰', label: 'Slovak', nativeName: 'Slovenčina' },
  { code: 'uk', flag: '🇺🇦', label: 'Ukrainian', nativeName: 'Українська' },
];

export default function LanguageSwitcher() {
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLocaleData = locales.find(l => l.code === currentLocale) || locales[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLocaleChange = (locale: Locale) => {
    setIsOpen(false);

    // Simple approach: just use router.replace with current pathname
    // next-intl will handle locale switching automatically
    router.replace(
      // @ts-ignore
      { pathname, params },
      { locale }
    );
  };

  return (
    <div className="language-switcher" ref={dropdownRef}>
      <button
        className="language-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="language-flag">{currentLocaleData.flag}</span>
        <span className="language-code">{currentLocaleData.code.toUpperCase()}</span>
        <span className="language-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="language-dropdown">
          {locales.map((locale) => (
            <button
              key={locale.code}
              className={`language-option ${locale.code === currentLocale ? 'active' : ''}`}
              onClick={() => handleLocaleChange(locale.code)}
              aria-label={`Switch to ${locale.label}`}
            >
              <span className="language-flag">{locale.flag}</span>
              <div className="language-info">
                <span className="language-native">{locale.nativeName}</span>
                <span className="language-name">{locale.label}</span>
              </div>
              {locale.code === currentLocale && (
                <span className="language-check">✓</span>
              )}
            </button>
          ))}
        </div>
      )}

      <style jsx>{`
        .language-switcher {
          position: relative;
        }

        .language-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: var(--panel-2);
          border: 1px solid var(--border);
          padding: 8px 12px;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          color: var(--text-1);
        }

        .language-btn:hover {
          background: var(--accent);
          transform: scale(1.02);
        }

        .language-flag {
          font-size: 18px;
          line-height: 1;
        }

        .language-code {
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .language-arrow {
          font-size: 10px;
          opacity: 0.6;
          margin-left: 2px;
        }

        .language-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          box-shadow: var(--elevation-3);
          min-width: 200px;
          overflow: hidden;
          z-index: 1000;
          animation: slideDown 0.2s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .language-option {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: transparent;
          cursor: pointer;
          transition: all 0.15s ease;
          text-align: left;
          color: var(--text-1);
        }

        .language-option:hover {
          background: var(--accent-hover);
        }

        .language-option.active {
          background: var(--accent);
        }

        .language-option:not(:last-child) {
          border-bottom: 1px solid var(--border);
        }

        .language-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
        }

        .language-native {
          font-weight: 600;
          font-size: 14px;
          color: var(--text-1);
        }

        .language-name {
          font-size: 12px;
          color: var(--text-2);
        }

        .language-check {
          color: var(--accent-primary);
          font-weight: bold;
          font-size: 16px;
        }

        @media (max-width: 768px) {
          .language-btn {
            padding: 6px 10px;
            gap: 4px;
          }

          .language-flag {
            font-size: 16px;
          }

          .language-code {
            display: none;
          }

          .language-dropdown {
            min-width: 180px;
            right: -8px;
          }

          .language-option {
            padding: 10px 12px;
          }

          .language-native {
            font-size: 13px;
          }

          .language-name {
            font-size: 11px;
          }
        }
      `}</style>
    </div>
  );
}
